import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Button, Divider, Grid, Menu, Pagination, Select } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { exportSaleReturnsToPDF } from "../../utils/exportSaleReturn";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";

const firstCurrencyValue = (obj) => {
  if (obj && typeof obj === "object") {
    const firstEntry = Object.values(obj)[0];
    if (firstEntry && typeof firstEntry === "object")
      return firstEntry.source ?? "-";
    return firstEntry ?? "-";
  }
  return obj ?? "-";
};

function Row({ row }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const itemCount = row.return_items?.length ?? 0;

  const currency = row.sale_details
    ? Object.keys(row.sale_details.total_amount || {})[0] || ""
    : "";

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell>{row.sale_details?.id ? `Sale #${row.sale_details.id}` : "-"}</TableCell>
        <TableCell>{row.return_date?.slice(0, 10) ?? "-"}</TableCell>
        <TableCell>{row.reason ?? "-"}</TableCell>
        <TableCell>
          {firstCurrencyValue(row.sale_details?.total_amount)} {currency}
        </TableCell>
        <TableCell>
          {row.total_refound_amount ?? "-"} {currency}
        </TableCell>
        <TableCell>{itemCount}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                {t("ReportIndex.returnSale_collapse_title")}
              </Typography>

              <Table size="small" aria-label="return-items">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("ReportIndex.returnSale_collapse_product")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.returnSale_collapse_qty")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.returnSale_collapse_price")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.returnSale_collapse_refundAmount")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.return_items?.map((item) => {
                    const salePrice = firstCurrencyValue(
                      item.sale_item_details?.sale_price
                    );
                    return (
                      <TableRow key={item.id}>
                        <TableCell component="th" scope="row">
                          {item.sale_item_details?.product?.name}
                        </TableCell>
                        <TableCell>{item.return_quantity}</TableCell>
                        <TableCell>{salePrice}</TableCell>
                        <TableCell>{item.refund_amount}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    sale_details: PropTypes.object,
    return_date: PropTypes.string,
    reason: PropTypes.string,
    total_refound_amount: PropTypes.string,
    return_items: PropTypes.array,
  }).isRequired,
};

const ALL_SHOPS_OPTION = { id: "", name: "All Shops" };

export default function ReturnTable() {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();
  const allCustomersOption = React.useMemo(
    () => ({ id: "", name: t("ReportIndex.all_customers", "All Customers") }),
    [t]
  );

  const [selectedPeriod, setSelectedPeriod] = React.useState("custom");
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [selectedSaleId, setSelectedSaleId] = React.useState("");
  const [selectedCustomerId, setSelectedCustomerId] = React.useState("");
  const [selectedShop, setSelectedShop] = React.useState("");
  const [shopSearchTerm, setShopSearchTerm] = React.useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = React.useState("");
  const [selectedCustomerOption, setSelectedCustomerOption] = React.useState(
    allCustomersOption
  );
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  React.useEffect(() => {
    setPage(1);
  }, [startDate, endDate, selectedSaleId, selectedCustomerId, selectedShop, selectedPeriod]);

  const formatDateParam = React.useCallback((value) => {
    if (!value || !dayjs(value).isValid()) return "";
    return dayjs(value).format("YYYY-MM-DD");
  }, []);

  const getDateRangeForPeriod = React.useCallback((period) => {
    const today = dayjs();

    switch (period) {
      case "daily":
        return { start: today, end: today };
      case "weekly":
        return { start: today.startOf("week"), end: today.endOf("week") };
      case "monthly":
        return { start: today.startOf("month"), end: today.endOf("month") };
      case "yearly":
        return { start: today.startOf("year"), end: today.endOf("year") };
      default:
        return { start: null, end: null };
    }
  }, []);

  const applyPeriod = React.useCallback(
    (period) => {
      setSelectedPeriod(period);
      setPage(1);

      if (period === "custom") {
        setStartDate(null);
        setEndDate(null);
        return;
      }

      const { start, end } = getDateRangeForPeriod(period);
      setStartDate(start);
      setEndDate(end);
    },
    [getDateRangeForPeriod]
  );

  const buildQueryString = React.useCallback((includePagination = true) => {
    const params = new URLSearchParams();
    const start = formatDateParam(startDate);
    const end = formatDateParam(endDate);

    if (start && end && dayjs(start).isAfter(dayjs(end))) {
      params.set("start_date", end);
      params.set("end_date", start);
    } else {
      if (start) params.set("start_date", start);
      if (end) params.set("end_date", end);
    }

    if (selectedSaleId) params.set("sale", selectedSaleId);
    if (selectedCustomerId) params.set("customer", selectedCustomerId);
    if (selectedShop) params.set("shop", selectedShop);

    if (!includePagination) {
      params.set("page_size", "all");
      return params.toString();
    }

    params.set("page", String(page));
    if (rowsPerPage !== -1) {
      params.set("page_size", String(rowsPerPage));
    } else {
      params.set("page_size", "all");
    }
    return params.toString();
  }, [
    endDate,
    formatDateParam,
    page,
    rowsPerPage,
    selectedCustomerId,
    selectedSaleId,
    selectedShop,
    startDate,
  ]);

  const queryString = React.useMemo(
    () => buildQueryString(true),
    [buildQueryString]
  );

  const exportQueryString = React.useMemo(
    () => buildQueryString(false),
    [buildQueryString]
  );

  const {
    data: returnsData,
    isLoading,
    isError,
    error,
  } = useFetchObjects(
    ["sale-returns", queryString],
    `shop/sale-returns/?${queryString}`,
    token
  );

  const { data: exportReturnsData } = useFetchObjects(
    ["sale-returns-export", exportQueryString],
    `shop/sale-returns/?${exportQueryString}`,
    token
  );

  // Fetch shops for filter dropdown
  const { data: shopsData, isLoading: shopsLoading } = useFetchObjects(
    ["shops", "return-report-filter", shopSearchTerm],
    `shop/shop/?search=${encodeURIComponent(shopSearchTerm.trim())}&page_size=20`,
    token
  );

  // Fetch customers for filter dropdown - filter by selected shop
  const customerFetchUrl = React.useMemo(() => {
    let url = "shop/customers?page_size=all";
    if (selectedShop) {
      url += `&shop=${selectedShop}`;
    }
    return url;
  }, [selectedShop]);

  const { data: customersData, isLoading: customersLoading } = useFetchObjects(
    ["customers", "return-report-filter", selectedShop],
    customerFetchUrl,
    token
  );

  const { data: salesData } = useFetchObjects(
    ["sales", "return-report-filter"],
    "shop/sales/?page_size=all",
    token
  );

  const customers = React.useMemo(() => {
    if (Array.isArray(customersData?.results)) return customersData.results;
    if (Array.isArray(customersData)) return customersData;
    return [];
  }, [customersData]);
  const sales = Array.isArray(salesData?.results)
    ? salesData.results
    : Array.isArray(salesData)
      ? salesData
      : [];

  const returns = React.useMemo(() => {
    if (Array.isArray(returnsData?.results)) return returnsData.results;
    if (Array.isArray(returnsData)) return returnsData;
    return [];
  }, [returnsData]);
  const exportReturns = React.useMemo(() => {
    if (Array.isArray(exportReturnsData?.results)) return exportReturnsData.results;
    if (Array.isArray(exportReturnsData)) return exportReturnsData;
    return returns;
  }, [exportReturnsData, returns]);

  const totalCount = Number(returnsData?.count ?? returns.length);
  const finalCount = totalCount;
  const displayReturns = returns;
  const filteredReturns = exportReturns;
  // Prepare shop options
  const baseShopOptions = React.useMemo(
    () => (Array.isArray(shopsData) ? shopsData : shopsData?.results || []),
    [shopsData]
  );
  const shopOptions = React.useMemo(
    () => [ALL_SHOPS_OPTION, ...baseShopOptions],
    [baseShopOptions]
  );
  const selectedShopOption = React.useMemo(
    () =>
      shopOptions.find((shop) => String(shop.id) === String(selectedShop)) ||
      ALL_SHOPS_OPTION,
    [shopOptions, selectedShop]
  );

  const customerOptions = React.useMemo(
    () => [allCustomersOption, ...customers],
    [allCustomersOption, customers]
  );

  // Calculate summary totals
  const summaryTotals = React.useMemo(() => {
    return exportReturns.reduce((acc, ret) => {
      const saleTotal = parseFloat(firstCurrencyValue(ret.sale_details?.total_amount)) || 0;
      const refundTotal = parseFloat(ret.total_refound_amount) || 0;
      
      return {
        totalSaleAmount: acc.totalSaleAmount + saleTotal,
        totalRefundAmount: acc.totalRefundAmount + refundTotal
      };
    }, { totalSaleAmount: 0, totalRefundAmount: 0 });
  }, [exportReturns]);

  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `sale_returns_${today}`;

    if (type === "pdf") exportSaleReturnsToPDF(filteredReturns, `${base}.pdf`);
    handleClose();
  };

  const resetPageAndSet = (setter) => (value) => {
    setSelectedPeriod("custom");
    setter(value);
    setPage(1);
  };

  if (isLoading)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );

  if (isError)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error">
          Error fetching data: {error?.message || 'Unknown error'}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          API URL: {process.env.REACT_APP_API_BASE_URL}/shop/sale-returns/
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Token: {token ? 'Present' : 'Missing'}
        </Typography>
        <RefreshIcon 
          sx={{ cursor: "pointer", verticalAlign: "middle", mt: 2 }} 
          onClick={() => window.location.reload()}
        />
      </Box>
    );

  return (
    <>
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <FormControl fullWidth>
                  <Select
                    value={selectedPeriod}
                    onChange={(e) => applyPeriod(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="custom">
                      {t("ReportIndex.period_custom", "Custom Range")}
                    </MenuItem>
                    <MenuItem value="daily">
                      {t("ReportIndex.period_daily", "Daily")}
                    </MenuItem>
                    <MenuItem value="weekly">
                      {t("ReportIndex.period_weekly", "Weekly")}
                    </MenuItem>
                    <MenuItem value="monthly">
                      {t("ReportIndex.period_monthly", "Monthly")}
                    </MenuItem>
                    <MenuItem value="yearly">
                      {t("ReportIndex.period_yearly", "Yearly")}
                    </MenuItem>
                  </Select>
                </FormControl>
                <DatePicker
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={resetPageAndSet(setStartDate)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={resetPageAndSet(setEndDate)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <FormControl fullWidth>
                  <Select
                    value={selectedSaleId}
                    onChange={(e) => {
                      setSelectedSaleId(e.target.value);
                      setPage(1);
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">{t("ReportIndex.all_sale_ids")}</MenuItem>
                    {sales.map((sale) => (
                      <MenuItem key={sale.id} value={String(sale.id)}>
                        Sale #{sale.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Autocomplete
                  fullWidth
                  options={shopOptions}
                  value={selectedShopOption}
                  onChange={(event, newValue) => {
                    const nextId = newValue?.id ? String(newValue.id) : "";
                    setSelectedShop(nextId);
                    setShopSearchTerm(newValue?.name || "");
                    setSelectedCustomerId("");
                    setSelectedCustomerOption(allCustomersOption);
                    setCustomerSearchTerm("");
                    setPage(1);
                  }}
                  onInputChange={(event, value, reason) => {
                    if (reason === "input") setShopSearchTerm(value);
                    if (reason === "clear") {
                      setShopSearchTerm("");
                      setSelectedShop("");
                      setSelectedCustomerId("");
                      setSelectedCustomerOption(allCustomersOption);
                      setCustomerSearchTerm("");
                      setPage(1);
                    }
                  }}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id ?? "") === String(value?.id ?? "")
                  }
                  loading={shopsLoading}
                  clearOnBlur={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("ReportIndex.shop", "Shop")}
                    />
                  )}
                />
                <FormControl fullWidth>
                  <Select
                    value={selectedCustomerId}
                    onChange={(e) => {
                      setSelectedCustomerId(e.target.value);
                      const nextCustomer = customers.find(
                        (customer) => String(customer.id) === String(e.target.value)
                      );
                      setSelectedCustomerOption(
                        nextCustomer || allCustomersOption
                      );
                      setCustomerSearchTerm(nextCustomer?.name || "");
                      setPage(1);
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">{t("ReportIndex.all_customers")}</MenuItem>
                    {(customers || []).map((customer) => (
                      <MenuItem key={customer.id} value={String(customer.id)}>
                        {customer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Autocomplete
                  fullWidth
                  options={customerOptions}
                  value={selectedCustomerOption}
                  inputValue={customerSearchTerm}
                  onChange={(event, newValue) => {
                    const nextOption =
                      newValue && String(newValue.id ?? "") === ""
                        ? allCustomersOption
                        : newValue || allCustomersOption;

                    setSelectedCustomerOption(nextOption);
                    setSelectedCustomerId(
                      nextOption?.id ? String(nextOption.id) : ""
                    );
                    setCustomerSearchTerm(nextOption?.name || "");
                    setPage(1);
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      setCustomerSearchTerm(newInputValue);
                      if (
                        selectedCustomerId ||
                        selectedCustomerOption?.id === "" ||
                        selectedCustomerOption?.name !== newInputValue
                      ) {
                        setSelectedCustomerId("");
                        setSelectedCustomerOption(null);
                        setPage(1);
                      }
                    }

                    if (reason === "clear") {
                      setCustomerSearchTerm("");
                      setSelectedCustomerId("");
                      setSelectedCustomerOption(allCustomersOption);
                      setPage(1);
                    }
                  }}
                  loading={customersLoading}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id ?? "") === String(value?.id ?? "")
                  }
                  clearOnBlur={false}
                  openOnFocus
                  autoHighlight
                  filterOptions={(options, state) => {
                    const searchValue = state.inputValue.trim().toLowerCase();

                    if (!searchValue) {
                      return options;
                    }

                    return options.filter((option) =>
                      (option?.name || "").toLowerCase().includes(searchValue)
                    );
                  }}
                  loadingText={t("global.loading", "Loading...")}
                  noOptionsText={t(
                    "sale.noCustomersFound",
                    "No customers found"
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("ReportIndex.customer_filter", "Customer")}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setSelectedPeriod("custom");
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedSaleId("");
                    setSelectedCustomerId("");
                    setSelectedShop("");
                    setShopSearchTerm("");
                    setCustomerSearchTerm("");
                    setSelectedCustomerOption(allCustomersOption);
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      "aria-labelledby": "export-button",
                    }}
                  >
                    <MenuItem onClick={() => handleExportClick("excel")}>
                      {t("ReportIndex.export_excel")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("none")}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      <TableContainer component={Paper}>
        <Table aria-label="sale returns table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={8} style={{ textAlign: "center" }}>
                <Typography variant="h4">
                  {t("ReportIndex.returnSale_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t("ReportIndex.returnSale_table_saleID", "Sale Reference")}</TableCell>
              <TableCell>{t("ReportIndex.returnSale_table_date", "Return Date")}</TableCell>
              <TableCell>{t("ReportIndex.returnSale_table_reason")}</TableCell>
              <TableCell>
                {t("ReportIndex.returnSale_table_saleTotal")}
              </TableCell>
              <TableCell>
                {t("ReportIndex.returnSale_table_refundTotal")}
              </TableCell>
              <TableCell>
                {t("ReportIndex.returnSale_table_itemCount")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayReturns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} style={{ textAlign: "center", padding: "20px" }}>
                  <Typography variant="h6" color="textSecondary">
                    {isLoading ? 'Loading...' : 'No return records found'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Total records from API: {totalCount}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Filtered records: {finalCount}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayReturns.map((ret) => (
                <Row key={ret.id} row={ret} />
              ))
            )}
            {/* Summary Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>{t('Summary')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalSaleAmount.toFixed(2)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalRefundAmount.toFixed(2)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid container justifyContent="center" alignItems="center" padding="10px">
        <Box display="flex" alignItems="center">
          <Typography>{t("expense.rowsPerPage", "Rows Per Page")}</Typography>
          <FormControl sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { border: "none" } } }}>
            <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={-1}>{t("reports.all")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {rowsPerPage !== -1 && (
          <Pagination
            count={Math.max(1, Math.ceil(finalCount / rowsPerPage))}
            page={Math.min(page, Math.max(1, Math.ceil(finalCount / rowsPerPage)))}
            onChange={(e, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
            disabled={finalCount === 0}
          />
        )}
      </Grid>
    </>
  );
}
